import type {
  RouteLocationRaw,
  RouteLocationResolved,
  RouteRecordRedirectOption,
  Router,
} from 'vue-router';
import { isSameOriginPath } from '@/utils/redirectUtil';
import { employeeCheckinRoutes, frontDeskRoutes, notFoundRoutes, onboardingRoutes } from '@/pages';
import { IS_MARKET_AU } from '@/market';

/** Tên record `/` — dùng để nhận ra "đích này chính là điểm vào". */
export const LANDING_ROUTE_NAME = 'landing';

/** Màn mở ra khi admin chưa cấu hình gì. Đây là NƠI DUY NHẤT biết giá trị này. */
// `au` không đăng ký employee-checkin (src/market) — Front Desk là điểm vào thay thế.
const DEFAULT_LANDING: RouteLocationRaw = { name: IS_MARKET_AU ? frontDeskRoutes.name : employeeCheckinRoutes.name };
/** Chỉ để spec khẳng định đúng object này, không phải một literal chép tay sẽ trôi khỏi nhau. */
export const DEFAULT_LANDING_FOR_TEST = DEFAULT_LANDING;

/**
 * Router được tạo trong factory `route()` của Quasar nên không có singleton ở tầng module;
 * `index.ts` nộp instance vào ngay sau khi tạo. Chỉ dùng để tra BẢNG route.
 */
let router: Router | null = null;
export const registerLandingRouter = (instance: Router) => {
  router = instance;
};

/**
 * Đích của `/`.
 *
 * `homeRoute` là cấu hình TOÀN HỆ THỐNG do admin back-office đặt (issue #396: singleton
 * `global-configs`, KHÔNG theo tiệm), FE nhận nó qua `GET /home/global-config`. Nó là chuỗi
 * tự do gõ tay trong màn admin, nên phải kiểm ba điều trước khi dùng — thiếu bất kỳ điều nào
 * thì về mặc định:
 *
 * 1. **Là path nội bộ** — admin gõ `//evil.com` là một open-redirect, và nó đến từ bảng quản trị
 *    chứ không phải URL nên còn khó thấy hơn.
 * 2. **Không phải chính `/`** — nhận nó là guard tự đẩy về mình, vue-router bỏ cuộc với "Detected
 *    an infinite redirection" và app không mở được. ⚠ Đây KHÔNG phải giả định: admin backend từng
 *    seed cứng `homeRoute: "/"` (#396), nên các DB cũ đang mang đúng giá trị này.
 * 3. **Có màn thật nhận** — trỏ tới màn đã xoá thì rơi vào catch-all, tức ném người dùng vào 404
 *    ngay lần mở app.
 */
export const resolveLandingTarget = (homeRoute?: string | null): RouteLocationRaw => {
  if (!homeRoute || !isSameOriginPath(homeRoute)) {
    return DEFAULT_LANDING;
  }
  if (!router) {
    // Chưa tra được bảng route: chỉ chặn được ca tự-trỏ-về-mình, và phải chặn — bỏ sót là app
    // không mở được, trong khi chặn nhầm chỉ là về màn mặc định. So phần PATH chứ không so nguyên
    // chuỗi: query/hash không đổi record nào cả, nên `/?x=1` vẫn là chính record landing (đo được:
    // nhận nó thì `router.push('/')` ném `RangeError: Maximum call stack size exceeded`).
    return homeRoute.split(/[?#]/)[0] === '/' ? DEFAULT_LANDING : homeRoute;
  }
  const destination = resolveFinalDestination(router, homeRoute);
  return destination && isLandingDestination(destination) ? homeRoute : DEFAULT_LANDING;
};

/** Trần số chặng. Bảng thật chỉ sâu một chặng; số này chỉ để không có vòng nào chạy mãi. */
const MAX_REDIRECT_HOPS = 5;

/**
 * Giải tới ĐÍCH CUỐI, không dừng ở chặng đầu.
 *
 * `router.resolve()` KHÔNG đi theo `redirect` của record: với một alias, thứ nó trả về là chính
 * record redirect — `name` là `undefined`, không có `components` — nên cả ba phép kiểm dưới đây
 * đều thấy "sạch" và giá trị lọt. Đo được với alias `/auth` (nay đã bỏ): `homeRoute = '/auth'` làm
 * MỌI người đã đăng nhập mở app ra thấy form đăng nhập, vì đây là cấu hình singleton toàn hệ thống.
 *
 * Đi theo chuỗi chứ KHÔNG từ chối mọi redirect: bảng thật hiện không còn alias nào, nhưng thêm một
 * cái trỏ vào màn thường thì phải đi qua được — ca giả lập ở `landing-redirect-chain.spec.ts`.
 *
 * Redirect dạng HÀM thì từ chối thẳng chứ KHÔNG gọi: hàm duy nhất trong bảng là redirect của chính
 * `/` (`routes.ts`), gọi nó ở đây là đệ quy ngược vào `resolveLandingTarget`.
 */
const resolveFinalDestination = (
  instance: Router,
  homeRoute: string,
): RouteLocationResolved | null => {
  let target: RouteLocationRaw = homeRoute;
  // `resolve` NÉM chứ không trả về `null` khi đích không giải được — `No match for {name:…}`, và
  // `Missing required param "id"` với alias trỏ tới route có param (vue-router tự bù `to.params`
  // khi nó đi redirect, còn vòng lặp này thì không). Chặng ĐẦU không bao giờ ném vì đó là chuỗi
  // path và luôn khớp catch-all; các chặng sau thì có. Ném ở đây là ném ra khỏi `router.push('/')`
  // — tức app không mở được, đúng thứ hàm này sinh ra để chặn.
  try {
    for (let hop = 0; hop <= MAX_REDIRECT_HOPS; hop += 1) {
      // Chú kiểu tường minh: `resolve` là overload, để TS tự suy ở đây sinh vòng suy kiểu (TS7022).
      const resolved: RouteLocationResolved = instance.resolve(target);
      const redirect: RouteRecordRedirectOption | undefined = resolved.matched.at(-1)?.redirect;
      if (!redirect) {
        return resolved;
      }
      if (typeof redirect === 'function') {
        return null;
      }
      target = redirect;
    }
  } catch {
    return null;
  }
  return null;
};

/**
 * "Màn này có được phép làm điểm hạ cánh không" — KHÁC hẳn "màn này có tồn tại không".
 *
 * Phép kiểm cũ chỉ loại `not-found`, nên mọi màn TRUNG CHUYỂN đều lọt, và mỗi cái hỏng một kiểu
 * (đo được cả ba):
 *
 * - `/store-picker` — `onPick` làm `router.replace('/')` khi không có `?redirect=`
 *   (`StorePicker.vue:107`), nên `/` → picker → `/` là vòng lặp: người có ĐÚNG MỘT tiệm bị
 *   auto-pick rồi kẹt vĩnh viễn, người nhiều tiệm bấm chọn mà không có gì xảy ra.
 * - `/onboarding` — người đã hoàn tất bị `requireAuth` đẩy về `/`, rồi `/` lại trả về
 *   `/onboarding`. Vòng lặp không có trần: cơ chế chống-lặp của vue-router chỉ so HAI chặng liền
 *   kề và chỉ chạy trong bản dev.
 * - Cụm màn auth (`/signin`, `/banned`, `/set-password`, …) — người ĐÃ đăng nhập mở app ra thấy
 *   form đăng nhập.
 *
 * Nhận diện cụm auth bằng named view `brand` mà `AuthLayout` yêu cầu — đây là bất biến mà spec hợp
 * đồng URL đã khoá, nên thêm màn auth mới thì luật tự phủ, không phải nhớ cập nhật danh sách.
 */
const isLandingDestination = (resolved: RouteLocationResolved): boolean => {
  const notFoundName = notFoundRoutes.children?.[0]?.name;
  if (resolved.name === notFoundName) {
    return false;
  }
  if (resolved.name === LANDING_ROUTE_NAME || resolved.name === onboardingRoutes.name) {
    return false;
  }
  const record = resolved.matched.at(-1);
  return !(record && 'components' in record && record.components?.brand);
};
